import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

import { getResponseMessage } from "../../../utils/handlers/get-response-message"
import { logout } from "../../LoginPage/redux/auth.thunks"
import type { ReportListItem } from "../types/types.ts"
import { reportsSliceName } from "./reports.constants"
import {
  createReport,
  deleteReport,
  duplicateReport,
  fetchReportDetails,
  fetchReports,
  renameReport,
  toggleReportFavorite,
  updateReportSettings
} from "./reports.thunks"
import type { ReportsFilters, ReportsState } from "./reports.types"

const initialState: ReportsState = {
  fetching: false,
  results: [],
  filters: {
    search: null,
    favorites: false,
    shared: false
  },
  currentRequestId: undefined,
  saveFetching: false,
  saveResult: null,
  detailFetching: false,
  detail: null,
  pendingDeletes: {},
  pendingRenames: {},
  latestRenameSeqById: {}
}

const setFavorite = (state: ReportsState, id: string, isFavorite: boolean) => {
  state.results = state.results.map((report) =>
    report.id === id ? { ...report, is_favorite: isFavorite } : report
  )
  if (state.detail?.id === id) {
    state.detail = { ...state.detail, is_favorite: isFavorite }
  }
}

const removeReport = (state: ReportsState, id: string) => {
  state.results = state.results.filter((report) => report.id !== id)
}

const mergeIntoResults = (
  state: ReportsState,
  id: string,
  fields: Partial<ReportListItem>
) => {
  state.results = state.results.map((report) =>
    report.id === id ? { ...report, ...fields } : report
  )
  if (state.detail?.id === id) state.detail = { ...state.detail, ...fields }
}

const reportsSlice = createSlice({
  name: reportsSliceName,
  initialState,
  reducers: {
    reportsSetFilters: (
      state,
      action: PayloadAction<Partial<ReportsFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    reportsClearSaveResult: (state) => {
      state.saveResult = null
    },
    reportsClearDetail: (state) => {
      state.detail = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReports.pending, (state, action) => {
        state.fetching = true
        state.currentRequestId = action.meta.requestId
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return
        state.fetching = false
        state.results = action.payload
      })
      .addCase(fetchReports.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return
        state.fetching = false
      })
      .addCase(createReport.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(createReport.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: "report_create",
          message: getResponseMessage(
            action.payload,
            "Report created successfully."
          ),
          id: action.payload?.details?.data?.id
        }
      })
      .addCase(createReport.rejected, (state) => {
        state.saveFetching = false
      })
      .addCase(fetchReportDetails.pending, (state) => {
        state.detailFetching = true
      })
      .addCase(fetchReportDetails.fulfilled, (state, action) => {
        state.detailFetching = false
        state.detail = action.payload
      })
      .addCase(fetchReportDetails.rejected, (state) => {
        state.detailFetching = false
      })
      .addCase(toggleReportFavorite.pending, (state, action) => {
        setFavorite(
          state,
          action.meta.arg.id,
          !action.meta.arg.previousIsFavorite
        )
      })
      .addCase(toggleReportFavorite.fulfilled, (state, action) => {
        setFavorite(state, action.payload.id, action.payload.is_favorite)
        if (state.filters.favorites && !action.payload.is_favorite) {
          removeReport(state, action.payload.id)
        }
      })
      .addCase(toggleReportFavorite.rejected, (state, action) => {
        setFavorite(
          state,
          action.meta.arg.id,
          action.meta.arg.previousIsFavorite
        )
      })
      .addCase(duplicateReport.fulfilled, (state, action) => {
        if (action.payload) state.results = [action.payload, ...state.results]
      })
      .addCase(renameReport.pending, (state, action) => {
        const { id, name, previousName } = action.meta.arg
        const seq = (state.latestRenameSeqById[id] ?? 0) + 1
        state.latestRenameSeqById[id] = seq
        state.pendingRenames[action.meta.requestId] = { id, previousName, seq }
        mergeIntoResults(state, id, { name })
      })
      .addCase(renameReport.fulfilled, (state, action) => {
        delete state.pendingRenames[action.meta.requestId]
      })
      .addCase(renameReport.rejected, (state, action) => {
        const pending = state.pendingRenames[action.meta.requestId]
        if (!pending) return
        delete state.pendingRenames[action.meta.requestId]
        if (state.latestRenameSeqById[pending.id] === pending.seq)
          mergeIntoResults(state, pending.id, { name: pending.previousName })
      })
      // Prefer the saved report the PATCH echoes back — it also refreshes
      // updated_at, which the list renders.
      .addCase(updateReportSettings.fulfilled, (state, action) => {
        const { id, patch, report } = action.payload
        mergeIntoResults(state, id, report ?? patch)
      })
      .addCase(deleteReport.pending, (state, action) => {
        const index = state.results.findIndex(
          (report) => report.id === action.meta.arg.id
        )
        if (index === -1) return
        state.pendingDeletes[action.meta.requestId] = {
          report: state.results[index],
          index
        }
        state.results = state.results.filter(
          (report) => report.id !== action.meta.arg.id
        )
      })
      .addCase(deleteReport.fulfilled, (state, action) => {
        delete state.pendingDeletes[action.meta.requestId]
      })
      .addCase(deleteReport.rejected, (state, action) => {
        const pending = state.pendingDeletes[action.meta.requestId]
        if (pending) {
          const next = [...state.results]
          next.splice(pending.index, 0, pending.report)
          state.results = next
          delete state.pendingDeletes[action.meta.requestId]
        }
      })
      .addCase(logout.fulfilled, () => initialState)
  }
})

export const { reportsSetFilters, reportsClearSaveResult, reportsClearDetail } =
  reportsSlice.actions

export default reportsSlice
